import React, { useCallback, useMemo, useState } from "react";
import Text from "@ui/Text";
import { css } from "@linaria/core";
import { colors, spacings } from "@ui/Theme";
import t from "../../../../../../../../../i18n";
import type { Project } from "../../../../../../../../../api/useProjects";
import formatDate from "@ui/i18n/formatDate";
import ModalForm from "@ui/ModalV2/Form";
import { usePost } from "../../../../../../../../../api/useCall";
import useToasts from "@ui/Toast/useToasts";
import { DatePicker } from "@ui/DatePicker";
import { useProjectPeriod } from "../../../../../../../useProjectPeriod";
import { useProjectSource } from "../../../../../../../../../api/useSources";
import { parseAbsolute, toCalendarDate, type DateValue } from "@internationalized/date";
import type { ManualFaultEvent } from "../../../../../../../../../api/useManualFaultEvents";
import SignedNumberInput, { getSignedNumberInputError } from "../../SignedNumberInput";

interface Impact {
  quantityId: string;
  quantityName: string;
  unit: string | null;
  value: number;
}
interface ModifyModalFormProps {
  period: boolean;
  project: Project;
  manualFaultEvent: ManualFaultEvent;
  timeWindow: {
    id: string;
    startDate: number;
    endDate: number;
    impacts: Impact[];
  };
  closeModal: () => void;
}

const ModifyModalForm = ({
  period,
  project,
  manualFaultEvent,
  timeWindow,
  closeModal,
}: ModifyModalFormProps) => {
  const { addToast } = useToasts();
  const replaceManualFaultEventImpacts = usePost(
    "/projects/:projectId(uuid)/manualFaultEvents/:manualFaultEventId(uuid)/windows/:windowId(uuid)/impacts",
  );
  const source = useProjectSource(project);
  if (!source) throw new Error("No source found for the project");
  const [, , projectExtremes] = useProjectPeriod({ source, project, boundaries: "analyses" });

  const impactDefaultValue = useMemo(
    () =>
      timeWindow.impacts.reduce(
        (acc, impact) => {
          acc[impact.quantityId] = String(impact.value);
          return acc;
        },
        {} as Record<string, string>,
      ),
    [timeWindow.impacts],
  );
  const [impactInputs, setImpactInputs] = useState(impactDefaultValue);
  const [errorInputs, setErrorInputs] = useState(
    timeWindow.impacts.reduce(
      (acc, impact) => {
        acc[impact.quantityId] = null;
        return acc;
      },
      {} as Record<string, string | null>,
    ),
  );
  const handleErrorInput = useCallback((value: string): string | null => {
    if (getSignedNumberInputError(value))
      return t("project.events.manualFaultEvent.modifyModalForm.errors.should_be_digital");
    return null;
  }, []);

  /** Section Date */
  const defaultStartDate = useMemo(
    () => parseAbsolute(new Date(timeWindow.startDate).toISOString(), project.timezone),
    [timeWindow.startDate, project.timezone],
  );
  const [startsAt, setStartsAt] = React.useState<DateValue | null>(defaultStartDate);
  const dateBoundaries = React.useMemo(() => {
    const startDate = new Date(manualFaultEvent.startsAt.getTime());
    startDate.setHours(0, 0, 0, 0);
    startDate.setDate(startDate.getDate() + 1);

    const endDate = new Date(manualFaultEvent.metadata.endsAt.getTime());
    endDate.setHours(23, 59, 59, 999);

    return {
      startDate,
      endDate,
      startDateValue: parseAbsolute(startDate.toISOString(), project.timezone),
      endDateValue: parseAbsolute(endDate.toISOString(), project.timezone),
    };
  }, [manualFaultEvent, project.timezone]);
  const onStartsAtDatePickerChange = React.useCallback(
    (value: DateValue | null) => {
      const resValue = value;
      if (resValue === null) {
        setStartsAt(null);
      } else {
        resValue.set({
          hour: 0,
          minute: 0,
          second: 0,
        });
        const date = toCalendarDate(resValue).toDate(project.timezone);
        setStartsAt(parseAbsolute(date.toISOString(), project.timezone));
      }
    },
    [project.timezone],
  );
  const errorStartedDate = React.useMemo(() => {
    if (startsAt === null || period === false) return;

    if (
      project.analysesAggregate?.latestActiveAnalysis &&
      startsAt >
        parseAbsolute(project.analysesAggregate.latestActiveAnalysis.date, project.timezone)
    ) {
      return t(
        "project.events.manual_fault.window.startsAt.error.after_latest_analysis",
        formatDate(
          new Date(project.analysesAggregate.latestActiveAnalysis.date),
          "slash",
          project.timezone,
        ),
      );
    }

    const currentWindowIndex = manualFaultEvent.windows.findIndex(w => w.id === timeWindow.id);
    const previousWindow = manualFaultEvent.windows.at(currentWindowIndex - 1);
    const previousStartsAt = previousWindow?.startsAt;

    const nextWindow = manualFaultEvent.windows.at(currentWindowIndex + 1);
    const nextStartsAt = nextWindow?.startsAt;

    if (
      previousWindow &&
      previousWindow.id !== timeWindow.id &&
      previousStartsAt &&
      startsAt <= parseAbsolute(previousStartsAt.toISOString(), project.timezone)
    ) {
      return t(
        "project.events.manual_fault.window.startsAt.error.previous_existing_period",
        formatDate(previousStartsAt, "slash", project.timezone),
      );
    }

    if (startsAt < dateBoundaries.startDateValue)
      return t(
        "project.events.manual_fault.window.startsAt.error.before_event_start",
        formatDate(dateBoundaries.startDate, "slash", project.timezone),
      );

    if (
      nextWindow &&
      nextWindow.id !== timeWindow.id &&
      nextStartsAt &&
      startsAt >= parseAbsolute(nextStartsAt.toISOString(), project.timezone)
    ) {
      return t(
        "project.events.manual_fault.window.startsAt.error.next_existing_period",
        formatDate(nextStartsAt, "slash", project.timezone),
      );
    }

    return;
  }, [
    period,
    startsAt,
    dateBoundaries.startDateValue,
    dateBoundaries.startDate,
    project.timezone,
    manualFaultEvent.windows,
    timeWindow.id,
    project.analysesAggregate,
  ]);

  const resetValues = useCallback(() => {
    setImpactInputs(impactDefaultValue);
  }, [setImpactInputs, impactDefaultValue]);

  const handleSubmit = useCallback(async () => {
    const impacts = Object.entries(impactInputs).map(([quantityId, value]) => ({
      quantityId,
      value: parseFloat(value),
    }));

    try {
      await replaceManualFaultEventImpacts({
        routeParams: {
          projectId: project.id,
          manualFaultEventId: manualFaultEvent.id,
          windowId: timeWindow.id,
        },
        body: {
          startsAt:
            period === false || startsAt === null
              ? undefined
              : toCalendarDate(startsAt).toDate(project.timezone),
          impacts,
        },
      });
      addToast("success", t("project.events.manualFaultEvent.modifyModalForm.toast.success"));
      closeModal();
      return true;
    } catch (error) {
      addToast("error", t("errors.backend_error"));
      return false;
    }
  }, [
    impactInputs,
    replaceManualFaultEventImpacts,
    project.id,
    manualFaultEvent.id,
    timeWindow,
    addToast,
    startsAt,
    project.timezone,
    period,
    closeModal,
  ]);

  const dirtyInputs = useMemo(
    () =>
      timeWindow.impacts.some(impact => {
        const currentValue = impactInputs[impact.quantityId];
        const defaultValue = impactDefaultValue[impact.quantityId];
        return defaultValue !== currentValue;
      }) ||
      (startsAt !== null && startsAt.compare(defaultStartDate) !== 0),
    [timeWindow.impacts, impactDefaultValue, impactInputs, startsAt, defaultStartDate],
  );
  const hasErrors = useMemo(
    () =>
      Object.values(errorInputs).some(error => error !== null) ||
      errorStartedDate !== undefined ||
      (period && startsAt === null),
    [errorInputs, errorStartedDate, period, startsAt],
  );

  return (
    <ModalForm
      cancellable
      onRequestClose={closeModal}
      onSubmit={dirtyInputs && !hasErrors ? handleSubmit : false}
      okButtonName={t("project.events.modalForm.okButton.modify")}
    >
      {period ? (
        <DatePicker
          id=""
          label={t("project.events.drawer.create_manual_event.date_picker.startsAt.label")}
          dateOptions={{
            shouldCloseOnSelect: true,
            hideTimeZone: true,
            granularity: "day",
            defaultValue: dateBoundaries.startDateValue,
            minValue: dateBoundaries.startDateValue,
            maxValue: dateBoundaries.endDateValue,
            value: startsAt,
            isDateUnavailable: date => {
              const normalize = (d: DateValue) =>
                d.set({ hour: 0, minute: 0, second: 0, millisecond: 0 });

              const normDate = normalize(date);
              const min = normalize(dateBoundaries.startDateValue);
              const max = dateBoundaries.endDateValue;

              if (normDate.compare(min) < 0) return true;
              if (normDate.compare(max) > 0) return true;
              if (
                manualFaultEvent.windows.some((window, index) => {
                  const winDate = normalize(
                    parseAbsolute(window.startsAt.toISOString(), project.timezone),
                  );

                  const previousWindow = manualFaultEvent.windows.at(index - 1);

                  if (window.id === timeWindow.id) return false;

                  if (previousWindow && previousWindow.id === timeWindow.id) {
                    return normDate.compare(winDate) >= 0;
                  }

                  return normDate.compare(winDate) <= 0;
                })
              )
                return true;
              return false;
            },
            onChange: onStartsAtDatePickerChange,
          }}
          revert={() => setStartsAt(defaultStartDate)}
          revertable={startsAt !== defaultStartDate}
          error={errorStartedDate}
          disabled={
            formatDate(projectExtremes.from, "slash", project.timezone) ===
            formatDate(projectExtremes.to, "slash", project.timezone)
          }
          required
        />
      ) : (
        <span className={periodCss}>
          <Text type="body1SemiBold" className={title}>
            {t("project.events.manualFaultEvent.modifyModalForm.impactedPeriod")}
          </Text>
          <Text>
            {formatDate(timeWindow.startDate, "slash", project.timezone)} -{" "}
            {formatDate(timeWindow.endDate, "slash", project.timezone)}
          </Text>
        </span>
      )}
      <div className={impactPIs}>
        <Text type="body1SemiBold" className={title}>
          {t("project.events.manualFaultEvent.modifyModalForm.impactedPIs")}
        </Text>
        {timeWindow.impacts.map(impact => {
          const key = `${impact.quantityId}`;
          return (
            <SignedNumberInput
              className={input}
              key={key}
              placeholder={t("project.events.manualFaultEvent.modifyModalForm.input.placeholder")}
              value={impactInputs[impact.quantityId] ?? ""}
              label={`${impact.quantityName} ${impact.unit && `(${impact.unit})`}`}
              onValueChange={value => {
                setImpactInputs(prev => {
                  const existingImpact = prev[impact.quantityId];
                  if (existingImpact === undefined) return prev;

                  return {
                    ...prev,
                    [impact.quantityId]: value,
                  };
                });
                setErrorInputs(prev => ({
                  ...prev,
                  [impact.quantityId]: handleErrorInput(value),
                }));
              }}
              error={errorInputs[impact.quantityId] || undefined}
              defaultValue={impactDefaultValue[impact.quantityId]}
              revertable={impactDefaultValue[impact.quantityId] !== impactInputs[impact.quantityId]}
              revert={() => resetValues()}
            />
          );
        })}
      </div>
    </ModalForm>
  );
};

export default ModifyModalForm;

//#region CSS
const title = css`
  color: ${colors.carbon800};
`;
const periodCss = css`
  & > :last-child {
    margin-top: ${spacings.xxs};
  }
`;
const impactPIs = css`
  display: flex;
  flex-direction: column;
  margin-top: ${spacings.s};
  row-gap: ${spacings.m};
`;
const input = css`
  div:has(> input) {
    width: 110px;
  }
`;
//#endregion CSS
